import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Typography } from "../components/atoms/Typography";
import { Button } from "../components/atoms/Button";
import { Input } from "../components/atoms/Input";
import { ContractDocumentsTable } from "../components/organisms/ContractDocumentsTable";
import { UploadContractDrawer } from "../components/organisms/UploadContractDrawer";
import { uploadContract } from "../services/contractUploadService";
import { useContractCategories } from "../hooks/useContractCategories";
import type { ContractDocument } from "../types/contractDocument";
import {
  fetchContracts,
  type ContractsSortField,
} from "../services/contractsService";
import { ArrowLeft, Filter, Search, Upload } from "lucide-react";

/** Backend MAX_LIMIT for GET /contracts (see pagination clamp). */
const CONTRACTS_LIST_LIMIT = 100;

export const ContractListPage = () => {
  const navigate = useNavigate();
  const { categories: contractTypeFilterOptions, loading: categoriesLoading } = useContractCategories();
  const [documents, setDocuments] = useState<ContractDocument[]>([]);
  const [listTotal, setListTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [sortBy, setSortBy] = useState<ContractsSortField>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 350);
    return () => window.clearTimeout(handle);
  }, [searchTerm]);

  const loadDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { items, total } = await fetchContracts({
        q: debouncedSearch || undefined,
        contract_type: selectedType || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
        limit: CONTRACTS_LIST_LIMIT,
        offset: 0,
      });
      setDocuments(items);
      setListTotal(total);
    } catch {
      setError("Unable to load contracts. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, selectedType, sortBy, sortDir]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const handleServerSort = useCallback((field: ContractsSortField, direction: "asc" | "desc") => {
    setSortBy(field);
    setSortDir(direction);
  }, []);

  const handleUpload = useCallback(
    async (file: File, contractCategory: string) => {
      const result = await uploadContract(file, contractCategory);
      if (result.success) {
        setIsDrawerOpen(false);
        setSuccessMessage("Contract uploaded successfully.");
        const hideSuccess = () => setSuccessMessage(null);
        setTimeout(hideSuccess, 5000);
        try {
          await loadDocuments();
        } catch {
          setError("Contract uploaded, but the list could not be refreshed.");
        }
      } else {
        throw new Error(result.error ?? "Upload failed");
      }
    },
    [loadDocuments]
  );

  return (
    <section className="page-section">
      <button
        type="button"
        onClick={() => navigate("/contracts-selection")}
        className="back-link"
      >
        <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
        Back to Templates
      </button>

      <div className="page-header">
        <div>
          <Typography as="h1" variant="h1">
            Approved Contracts
          </Typography>
          <p className="page-subtitle">
            Select a contract to upload document
          </p>
        </div>
        <Button
          className="flex items-center gap-2"
          onClick={() => setIsDrawerOpen(true)}
        >
          <Upload className="h-4 w-4" />
          Upload Contract
        </Button>
      </div>

      <div className="page-toolbar">
        <div className="search-wrapper">
          <Search className="search-icon" />
          <Input
            type="text"
            placeholder="Search contracts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search contracts"
            className="bg-white pl-10"
          />
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFilter((prev) => !prev)}
            className={twMerge(
              "filter-btn",
              showFilter && "filter-btn--active"
            )}
            aria-expanded={showFilter}
          >
            <Filter className="h-4 w-4" />
            Filter
          </button>

          {showFilter && (
            <div className="filter-dropdown">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Contract Type
              </p>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                disabled={categoriesLoading}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-body focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">
                  {categoriesLoading ? "Loading types…" : "All types"}
                </option>
                {contractTypeFilterOptions.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {selectedType && (
                <button
                  type="button"
                  onClick={() => { setSelectedType(""); setShowFilter(false); }}
                  className="mt-2 text-xs font-medium text-primary-500 hover:text-primary-600"
                >
                  Clear filter
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {successMessage && (
        <div className="alert-success">{successMessage}</div>
      )}

      {error && (
        <div className="alert-error">{error}</div>
      )}

      {isLoading ? (
        <Typography variant="muted">Loading contracts…</Typography>
      ) : (
        <ContractDocumentsTable
          documents={documents}
          listTotal={listTotal}
          hasActiveSearch={debouncedSearch.length > 0}
          hasActiveTypeFilter={selectedType.length > 0}
          serverSortField={sortBy}
          serverSortDir={sortDir}
          onServerSort={handleServerSort}
        />
      )}

      <UploadContractDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onUpload={handleUpload}
      />
    </section>
  );
};
